"use client";

import { ChevronDown } from "lucide-react";
import Link from "next/link";

import { accountNav } from "@/components/shell/nav";
import {
  countForHref,
  NO_COUNTS,
} from "@/components/shell/nav-counts";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

import { useCurrentUser } from "@/features/auth/hooks/use-current-user";
import { useUnreadCount } from "@/features/chat/hooks/use-chat";
import { CountBadge } from "@/features/notifications/components/count-badge";
import { useNotificationCounts } from "@/features/notifications/hooks/use-notification-counts";

/**
 * The "My account" dropdown that sits *inside* the primary nav (between Docs and
 * Marketplace), matching the legacy top navbar where MY ACCOUNT was a nav item
 * rather than a right-hand control. Only renders when signed in. The trigger is
 * a nav pill; the menu lists the same account links as the legacy
 * `nav_dropdown` (General / Employer / Worker), each with its own count badge.
 * Sign-out lives on the right-hand user bar, so it is intentionally omitted here.
 */
export function AccountNavMenu() {
  const { user, isAuthenticated } = useCurrentUser();
  const { data: counts } = useNotificationCounts();
  const { data: chatUnread } = useUnreadCount({ enabled: isAuthenticated });

  if (!isAuthenticated || !user) return null;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className="inline-flex h-8 items-center gap-1 rounded-full px-3.5 text-sm font-medium text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          My account
          <ChevronDown aria-hidden="true" className="size-4" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        className="max-h-[min(80vh,34rem)] w-56 overflow-y-auto"
      >
        {accountNav.map((group) => (
          <DropdownMenuGroup key={group.label ?? "general"}>
            {group.label ? (
              <DropdownMenuLabel className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                {group.label}
              </DropdownMenuLabel>
            ) : null}
            {group.items.map((item) => {
              // The two sources settle independently — don't gate the chat
              // badge on the notification-counts poll having landed.
              const count = countForHref(
                item.href,
                counts ?? NO_COUNTS,
                chatUnread ?? 0,
              );
              return (
                <DropdownMenuItem key={item.href} asChild>
                  <Link
                    href={item.href}
                    className="flex items-center justify-between"
                  >
                    <span>{item.label}</span>
                    <CountBadge
                      count={count}
                      label={`${count} for ${item.label}`}
                    />
                  </Link>
                </DropdownMenuItem>
              );
            })}
            <DropdownMenuSeparator className="last:hidden" />
          </DropdownMenuGroup>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
